import { RefObject, useMemo, useRef } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import {
  NoDataReason,
  WorkloadCostReportItem,
  WorkloadCostReportResponse,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_COST_REPORT_QUERY_KEY = 'workloads-cost-report';

const getPaginatedWorkloadCostReportQueryKey = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  includeIdleCosts,
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit,
}: Partial<PaginatedWorkloadCostReportQueryArgs> = {}) =>
  [
    WORKLOADS_COST_REPORT_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          startTime,
          endTime,
          useListingPrices,
          includeIdleCosts,
          labels,
          namespaces,
          workloadNames,
          workloadTypes,
          pageLimit,
        ]
      : []),
  ] as const;

type PaginatedWorkloadCostReportQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  labels?: Array<{ label?: string; value?: string }>;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  pageLimit?: number;
  includeIdleCosts?: boolean;
  stepSeconds?: number;
};

const getPaginatedWorkloadCostReportQueryOptions = (
  {
    clusterId,
    startTime,
    endTime,
    useListingPrices,
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
    includeIdleCosts = false,
    pageLimit = 50,
    stepSeconds = 86_400,
  }: PaginatedWorkloadCostReportQueryArgs,
  metadataRef: RefObject<{
    noDataReason?: NoDataReason;
    count?: number;
  }>
) =>
  infiniteQueryOptions<WorkloadCostReportResponse>({
    queryKey: getPaginatedWorkloadCostReportQueryKey({
      clusterId,
      startTime,
      endTime,
      useListingPrices,
      includeIdleCosts,
      labels,
      namespaces,
      workloadNames,
      workloadTypes,
      pageLimit,
      stepSeconds,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getWorkloadsCostReport({
        clusterId,
        startTime,
        endTime,
        filter: { labels, namespaces, workloadNames, workloadTypes },
        stepSeconds,
        pageLimit: String(pageLimit),
        pageCursor: pageParam as string,
        useListingPrices,
        includeIdleResourceCosts: includeIdleCosts,
      });
      metadataRef.current = {
        noDataReason: data?.noDataReason,
        count: data?.count ? Number(data.count) : 0,
      };
      return data;
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export const usePaginatedWorkloadCostReportQuery = (
  args: PaginatedWorkloadCostReportQueryArgs
) => {
  const metadataRef = useRef<{ noDataReason?: NoDataReason; count?: number }>(
    {}
  );

  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(
    getPaginatedWorkloadCostReportQueryOptions(args, metadataRef)
  );

  const workloads: WorkloadCostReportItem[] = useMemo(
    () =>
      flatMap(data?.pages, (page) => page.items) as WorkloadCostReportItem[],
    [data]
  );

  return {
    workloads,
    count: metadataRef.current.count,
    noDataReason: metadataRef.current.noDataReason,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
